/**
 * Коридор первого входа (§6, §71).
 *
 * Один шаг на экране, одно действие. Никаких разделов: человек только что
 * зарегистрировался и не знает, что тут вообще есть, — и главное, что ему
 * нужно, это первый ролик.
 *
 * Шаг приходит с сервера и выводится из состояния, а не считается на клиенте:
 * человек может закрыть вкладку на проверке записи и вернуться через сутки.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { OnboardingStep } from '@alterium/shared';
import { onboardingApi } from '../api/onboarding';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';

/** Куда ведёт кнопка шага. Шаги-ожидания кнопки не имеют. */
const DESTINATION: Partial<Record<OnboardingStep, string>> = {
  capture: '/persona/capture',
  recapture: '/persona/capture',
  consent: '/persona/consent',
  look: '/looks',
  project: '/create',
};

/** Шаги, на которых мы ждём систему, а не человека. */
const WAITING: OnboardingStep[] = ['checking', 'avatar', 'voice'];

export function StartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const onboarding = useQuery({
    queryKey: ['onboarding'],
    queryFn: onboardingApi.get,
    // Пока идёт обработка, состояние меняется само — переспрашиваем.
    refetchInterval: (query) =>
      query.state.data && WAITING.includes(query.state.data.step) ? 4000 : false,
  });

  const leave = useMutation({
    mutationFn: onboardingApi.skip,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      navigate('/', { replace: true });
    },
  });

  const step = onboarding.data?.step;

  // Коридор пройден — дальше человеку нужен кабинет, а не эта страница.
  useEffect(() => {
    if (onboarding.data?.completed) navigate('/', { replace: true });
  }, [onboarding.data?.completed, navigate]);

  if (!step || onboarding.data?.completed) return <div className="min-h-full bg-page" />;

  const destination =
    step === 'render' && onboarding.data?.projectId
      ? `/projects/${onboarding.data.projectId}`
      : DESTINATION[step];

  const waiting = WAITING.includes(step);

  return (
    <div className="mx-auto flex min-h-full w-full max-w-lg flex-col justify-center gap-6 px-5 py-12">
      <header className="flex flex-col gap-1.5">
        <h1 className="text-page-title">{t('start.title')}</h1>
        <p className="text-body text-content-secondary">{t('start.subtitle')}</p>
      </header>

      <Card>
        <CardBody className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <p className="text-card-title">{t(`start.${step}.title`)}</p>
            <p className="text-body text-content-secondary">{t(`start.${step}.text`)}</p>
          </div>

          {waiting ? (
            /* Ожидание без кнопки: нажимать нечего, и кнопка тут только
               создавала бы ощущение, что человек чего-то не сделал. */
            <div className="h-1 w-full overflow-hidden rounded-full bg-surface-secondary">
              <div className="h-full w-1/3 animate-pulse rounded-full bg-accent" />
            </div>
          ) : null}

          {destination ? (
            <div className="flex flex-wrap gap-3">
              <Button variant="primary" onClick={() => navigate(destination)}>
                {t(`start.${step}.action`)}
              </Button>

              {onboarding.data?.canSkip ? (
                <Button onClick={() => navigate('/create')}>{t('start.look.skip')}</Button>
              ) : null}
            </div>
          ) : null}
        </CardBody>
      </Card>

      {/*
        Выход из коридора. Человек мог прийти за чем-то другим, и запирать
        его здесь навсегда хуже, чем показать кабинет раньше времени.
      */}
      <button
        type="button"
        onClick={() => leave.mutate()}
        disabled={leave.isPending}
        className="self-center text-meta text-content-muted transition-colors duration-fast hover:text-content"
      >
        {t('start.openCabinet')}
      </button>
    </div>
  );
}
